import { useId, useRef, useState } from 'react';

import { Badge, Button, Icon, IconButton } from '@ds/core';
import { Alert } from '@ds/feedback';
import { Checkbox, Input, Select, Textarea } from '@ds/forms';
import { abrirAnexo } from '@/comuns/abrirAnexo';
import {
    MEDIDAS_DA_PERIMETRIA, PROTOCOLOS_DE_ADIPOMETRIA, calcularAdipometria, calcularImc,
    calcularPerimetria, classificarPercentual, dobrasDoProtocolo, escalaEncadeadaPorSlug,
    medianaDaDobra, protocoloPorSlug, siglaDaEscala, silhuetaDoImc, tabelaPorSlug,
} from '@/data/blocosDaFicha';
import { arteDaOpcao, escalaPorSlug } from '@/data/escalasIlustradas';
import type { OpcaoDaEscala } from '@/data/escalasIlustradas';
import { MARCAS, alternarLista, aplicarMarca } from '@/data/textoRico';
import type { NomeDaMarca } from '@/data/textoRico';
import type { ArquivoDaFicha, CampoDeFicha, RespostaDaFicha, RespostaDeBloco } from '@/data/tipos';
import { TextoFormatado } from './TextoFormatado';

export interface BlocoDeRespostaProps {
    campo: CampoDeFicha;
    valor: RespostaDaFicha;
    onResponder: (valor: RespostaDaFicha) => void;
    /** A prévia mostra o mesmo controle, sem deixar responder. */
    desabilitado?: boolean;
    /** Onde o anexo é guardado. Ausente na prévia — lá não se envia arquivo. */
    onEnviarArquivo?: (arquivo: File) => Promise<ArquivoDaFicha>;
    /** O endereço assinado para ver um anexo já enviado. */
    onAbrirArquivo?: (caminho: string) => Promise<string>;
}

/**
 * O controle de um bloco da ficha.
 *
 * **Um só componente para o preenchimento e para a prévia.** Duas
 * implementações do mesmo bloco divergiriam, e a prévia passaria a mostrar uma
 * ficha que não é a que o profissional vai ver — que é justamente o que ela
 * existe para evitar.
 */
export function BlocoDeResposta({ campo, valor, onResponder, desabilitado = false, onEnviarArquivo, onAbrirArquivo }: BlocoDeRespostaProps) {
    switch (campo.tipo) {
        case 'texto-longo':
            return <Textarea value={String(valor ?? '')} onChange={onResponder} rows={3} aria-label={campo.rotulo} disabled={desabilitado} />;

        case 'texto-rico':
            return <EditorDeTexto rotulo={campo.rotulo} valor={String(valor ?? '')} onResponder={onResponder} desabilitado={desabilitado} />;

        case 'selecao-unica':
            return (
                <Select
                    value={String(valor ?? '')} onChange={onResponder} aria-label={campo.rotulo} disabled={desabilitado}
                    options={[{ value: '', label: 'Selecione' }, ...(campo.opcoes ?? []).filter((o) => o.trim()).map((o) => ({ value: o, label: o }))]}
                />
            );

        case 'selecao-multipla': {
            const marcadas = Array.isArray(valor) ? (valor as string[]) : [];
            const opcoes = (campo.opcoes ?? []).filter((o) => o.trim());
            if (!opcoes.length) return <SemConfiguracao texto="Este campo ainda não tem opções — quem preencher não terá o que escolher." />;
            return (
                <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                    {opcoes.map((opcao) => (
                        <Checkbox
                            key={opcao} label={opcao} checked={marcadas.includes(opcao)} disabled={desabilitado}
                            onChange={(m) => onResponder(m ? [...marcadas, opcao] : marcadas.filter((x) => x !== opcao))}
                        />
                    ))}
                </div>
            );
        }

        case 'escala':
            return <EscalaIlustradaCampo campo={campo} valor={String(valor ?? '')} onResponder={onResponder} desabilitado={desabilitado} />;

        case 'tabela-selecionavel':
            return <TabelaSelecionavelCampo campo={campo} valor={String(valor ?? '')} onResponder={onResponder} desabilitado={desabilitado} />;

        case 'escala-encadeada':
            return <EscalaEncadeadaCampo campo={campo} valor={(valor ?? {}) as RespostaDeBloco} onResponder={onResponder} desabilitado={desabilitado} />;

        case 'tabela':
            return <TabelaCampo campo={campo} valor={Array.isArray(valor) ? (valor as string[][]) : []} onResponder={onResponder} desabilitado={desabilitado} />;

        case 'calculo-imc':
            return <ImcCampo campo={campo} valor={(valor ?? {}) as RespostaDeBloco} onResponder={onResponder} desabilitado={desabilitado} />;

        case 'perimetria':
            return <PerimetriaCampo campo={campo} valor={(valor ?? {}) as RespostaDeBloco} onResponder={onResponder} desabilitado={desabilitado} />;

        case 'adipometria':
            return <AdipometriaCampo campo={campo} valor={(valor ?? {}) as RespostaDeBloco} onResponder={onResponder} desabilitado={desabilitado} />;

        case 'arquivo':
            return (
                <ArquivoCampo
                    rotulo={campo.rotulo}
                    valor={Array.isArray(valor) ? (valor as ArquivoDaFicha[]) : []}
                    onResponder={onResponder}
                    desabilitado={desabilitado || !onEnviarArquivo}
                    {...(onEnviarArquivo ? { onEnviarArquivo } : {})}
                    {...(onAbrirArquivo ? { onAbrirArquivo } : {})}
                />
            );

        default:
            return (
                <Input
                    type={campo.tipo === 'numero' ? 'number' : campo.tipo === 'data' ? 'date' : 'text'}
                    value={String(valor ?? '')}
                    onChange={(v) => onResponder(campo.tipo === 'numero' ? (v === '' ? undefined : Number(v)) : v)}
                    aria-label={campo.rotulo} disabled={desabilitado}
                />
            );
    }
}

function SemConfiguracao({ texto }: { texto: string }) {
    return (
        <p style={{ margin: 0, padding: '9px 12px', border: '1px dashed var(--border-strong)', borderRadius: 'var(--radius-xs)', fontSize: 13, color: 'var(--text-tertiary)' }}>
            {texto}
        </p>
    );
}

/* ---------------------------------------------------------- editor de texto */

function EditorDeTexto({ rotulo, valor, onResponder, desabilitado }: {
    rotulo: string; valor: string; onResponder: (v: string) => void; desabilitado: boolean;
}) {
    const area = useRef<HTMLTextAreaElement>(null);
    const [verComoFica, setVerComoFica] = useState(false);

    /* A marca é aplicada no texto e a seleção volta para onde estava: sem
       devolver o cursor, cada clique no negrito mandaria quem escreve de volta
       para o fim do campo. */
    const aplicar = (fn: (t: string, s: { inicio: number; fim: number }) => { texto: string; selecao: { inicio: number; fim: number } }) => {
        const el = area.current;
        if (!el) return;
        const r = fn(valor, { inicio: el.selectionStart, fim: el.selectionEnd });
        onResponder(r.texto);
        requestAnimationFrame(() => {
            el.focus();
            el.setSelectionRange(r.selecao.inicio, r.selecao.fim);
        });
    };

    const botao = (nome: NomeDaMarca) => (
        <IconButton
            key={nome} size="sm" variant="ghost" disabled={desabilitado}
            icon={<Icon name={MARCAS[nome].icone} size={14} />}
            label={`${MARCAS[nome].rotulo} (${MARCAS[nome].atalho})`}
            onClick={() => aplicar((t, s) => aplicarMarca(t, s, nome))}
        />
    );

    return (
        <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 2, padding: 4, borderBottom: '1px solid var(--border-subtle)', background: 'var(--surface-secondary)' }}>
                {botao('negrito')}
                {botao('italico')}
                <IconButton
                    size="sm" variant="ghost" disabled={desabilitado}
                    icon={<Icon name="list" size={14} />} label="Lista com marcadores"
                    onClick={() => aplicar((t, s) => alternarLista(t, s, false))}
                />
                <IconButton
                    size="sm" variant="ghost" disabled={desabilitado}
                    icon={<Icon name="list-ordered" size={14} />} label="Lista numerada"
                    onClick={() => aplicar((t, s) => alternarLista(t, s, true))}
                />
                <span style={{ marginLeft: 'auto' }}>
                    <Button size="sm" variant="ghost" onClick={() => setVerComoFica((v) => !v)}>
                        {verComoFica ? 'Escrever' : 'Ver como fica'}
                    </Button>
                </span>
            </div>

            {verComoFica ? (
                <div style={{ padding: 12, minHeight: 92, fontSize: 13.5, lineHeight: '20px' }}>
                    {valor.trim()
                        ? <TextoFormatado texto={valor} />
                        : <span style={{ color: 'var(--text-tertiary)' }}>Nada escrito ainda.</span>}
                </div>
            ) : (
                <textarea
                    ref={area} value={valor} rows={4} disabled={desabilitado} aria-label={rotulo}
                    onChange={(e) => onResponder(e.target.value)}
                    onKeyDown={(e) => {
                        if (!(e.ctrlKey || e.metaKey)) return;
                        if (e.key === 'b') { e.preventDefault(); aplicar((t, s) => aplicarMarca(t, s, 'negrito')); }
                        if (e.key === 'i') { e.preventDefault(); aplicar((t, s) => aplicarMarca(t, s, 'italico')); }
                    }}
                    placeholder="**Conduta:** aplicar 1 sessão&#10;- Retorno em 15 dias"
                    style={{
                        display: 'block', width: '100%', border: 0, resize: 'vertical', padding: 12,
                        font: 'inherit', fontFamily: 'var(--font-ui)', fontSize: 13.5, lineHeight: '20px',
                        color: 'var(--text-primary)', background: 'var(--surface)', outline: 'none',
                    }}
                />
            )}
        </div>
    );
}

/* ------------------------------------------------------- escala ilustrada */

function EscalaIlustradaCampo({ campo, valor, onResponder, desabilitado }: {
    campo: CampoDeFicha; valor: string; onResponder: (v: string) => void; desabilitado: boolean;
}) {
    const escala = escalaPorSlug(campo.escala);
    if (!escala) return <SemConfiguracao texto="Escolha qual escala este bloco usa." />;

    return (
        <div>
            <p style={{ margin: '0 0 8px', fontSize: 12.5, lineHeight: '18px', color: 'var(--text-secondary)' }}>
                {escala.descricao}
            </p>
            {escala.semArte ? (
                <p style={{ margin: '0 0 8px', fontSize: 11.5, color: 'var(--text-tertiary)' }}>
                    {escala.semArte === escala.opcoes.length
                        ? 'As ilustrações desta escala ainda não estão no acervo — as opções aparecem pelo nome.'
                        : `${escala.semArte} das ${escala.opcoes.length} opções ainda não têm ilustração.`}
                </p>
            ) : null}

            <div
                role="radiogroup" aria-label={campo.rotulo}
                style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(escala.colunas, 6)}, minmax(0, 1fr))`, gap: 8 }}
            >
                {escala.opcoes.map((opcao) => (
                    <OpcaoIlustrada
                        key={opcao.valor} opcao={opcao} marcada={valor === opcao.valor} desabilitado={desabilitado}
                        onEscolher={() => onResponder(valor === opcao.valor ? '' : opcao.valor)}
                    />
                ))}
            </div>
        </div>
    );
}

function OpcaoIlustrada({ opcao, marcada, desabilitado, onEscolher }: {
    opcao: OpcaoDaEscala; marcada: boolean; desabilitado: boolean; onEscolher: () => void;
}) {
    const arte = arteDaOpcao(opcao);
    return (
        <button
            type="button" role="radio" aria-checked={marcada} disabled={desabilitado}
            onClick={onEscolher}
            title={opcao.dica || undefined}
            style={{
                display: 'flex', flexDirection: 'column', gap: 6, padding: 6, cursor: desabilitado ? 'default' : 'pointer',
                border: `1.5px solid ${marcada ? 'var(--burgundy-500)' : 'var(--border)'}`,
                borderRadius: 'var(--radius-sm)', background: marcada ? 'var(--surface-brand-soft)' : 'var(--surface)',
                font: 'inherit', color: 'var(--text-primary)', textAlign: 'center', transition: 'var(--transition-control)',
            }}
        >
            <span
                style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    aspectRatio: '1 / 1', borderRadius: 'var(--radius-xs)', overflow: 'hidden',
                    background: opcao.cor ?? 'var(--surface-secondary)',
                }}
            >
                {arte
                    ? <img src={arte} alt={opcao.alt} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                    : opcao.cor
                        ? null
                        : <Icon name="image-off" size={16} style={{ color: 'var(--neutral-400)' }} />}
            </span>
            <span style={{ fontSize: 11.5, fontWeight: marcada ? 600 : 500, lineHeight: '15px' }}>{opcao.rotulo}</span>
            {opcao.dica && <span style={{ fontSize: 10.5, lineHeight: '14px', color: 'var(--text-tertiary)' }}>{opcao.dica}</span>}
        </button>
    );
}

/* --------------------------------------------------- tabela selecionável */

function TabelaSelecionavelCampo({ campo, valor, onResponder, desabilitado }: {
    campo: CampoDeFicha; valor: string; onResponder: (v: string) => void; desabilitado: boolean;
}) {
    const tabela = tabelaPorSlug(campo.tabela);
    if (!tabela) return <SemConfiguracao texto="Escolha qual tabela este bloco usa." />;

    return (
        <div>
            <p style={{ margin: '0 0 8px', fontSize: 12.5, color: 'var(--text-secondary)' }}>{tabela.descricao}</p>
            <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <caption style={{ captionSide: 'top', textAlign: 'left', padding: '0 0 6px', fontSize: 11.5, color: 'var(--text-tertiary)' }}>
                        {campo.rotulo}
                    </caption>
                    <thead>
                        <tr>
                            <th style={{ width: 1, padding: 0, borderBottom: '1px solid var(--border)' }}>
                                <span className="vec-sr-only">Escolha</span>
                            </th>
                            {tabela.colunas.map((c) => (
                                <th key={c} style={{ textAlign: 'left', padding: '8px 12px', fontSize: 11.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 'var(--tracking-wide)', color: 'var(--text-tertiary)', borderBottom: '1px solid var(--border)' }}>
                                    {c}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {tabela.linhas.map((linha) => {
                            const marcada = valor === linha.valor;
                            return (
                                /* A LINHA inteira é o alvo, e o rádio é de verdade — só não
                                   aparece. É a linha que o profissional lê para decidir, e o
                                   alvo tem de ser o que ele está lendo; mas quem navega por
                                   teclado ou leitor de tela precisa de um grupo de rádio que
                                   o navegador entenda, com as setas funcionando sozinhas. */
                                <tr
                                    key={linha.valor}
                                    onClick={() => !desabilitado && onResponder(marcada ? '' : linha.valor)}
                                    style={{
                                        cursor: desabilitado ? 'default' : 'pointer',
                                        background: marcada ? 'var(--burgundy-700)' : 'transparent',
                                        color: marcada ? '#fff' : 'var(--text-primary)',
                                    }}
                                >
                                    <td style={{ padding: 0, borderTop: '1px solid var(--border-subtle)' }}>
                                        <input
                                            type="radio" name={campo.uid} value={linha.valor}
                                            checked={marcada} disabled={desabilitado}
                                            onChange={() => onResponder(linha.valor)}
                                            aria-label={`${linha.celulas.join(' · ')}`}
                                            className="vec-sr-only"
                                        />
                                    </td>
                                    {linha.celulas.map((celula, i) => (
                                        <td key={`${linha.valor}-${String(i)}`} style={{ padding: '9px 12px', borderTop: '1px solid var(--border-subtle)' }}>
                                            {celula}
                                        </td>
                                    ))}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

/* ---------------------------------------------------- escala encadeada */

function EscalaEncadeadaCampo({ campo, valor, onResponder, desabilitado }: {
    campo: CampoDeFicha; valor: RespostaDeBloco; onResponder: (v: RespostaDaFicha) => void; desabilitado: boolean;
}) {
    const escala = escalaEncadeadaPorSlug(campo.escala);
    if (!escala) return <SemConfiguracao texto="Escolha qual escala encadeada este bloco usa." />;

    const respostas = escala.dimensoes.map((_, i) => String(valor[`d${String(i)}`] ?? ''));
    const sigla = siglaDaEscala(escala, respostas.filter(Boolean));

    const responder = (indice: number, escolhido: string) => {
        /* Mudar uma dimensão invalida as POSTERIORES. Sem isso, trocar a
           primeira resposta deixaria uma sigla montada com pedaços de um
           raciocínio anterior. */
        const proximas: RespostaDeBloco = {};
        escala.dimensoes.forEach((_, i) => {
            if (i < indice) proximas[`d${String(i)}`] = respostas[i];
            if (i === indice) proximas[`d${String(i)}`] = escolhido;
        });
        const completas = escala.dimensoes.map((_, i) => String(proximas[`d${String(i)}`] ?? '')).filter(Boolean);
        const nova = siglaDaEscala(escala, completas);
        onResponder(nova ? { ...proximas, sigla: nova } : proximas);
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-secondary)' }}>{escala.descricao}</p>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 10 }}>
                {escala.dimensoes.map((dimensao, i) => {
                    /* Cada select destrava o seguinte: responder fora de ordem
                       daria uma sigla sem o raciocínio que ela resume. */
                    const travado = desabilitado || (i > 0 && !respostas[i - 1]);
                    return (
                        <label key={dimensao.rotulo} style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12.5, fontWeight: 500, color: 'var(--text-secondary)' }}>
                            {dimensao.rotulo}
                            <Select
                                value={respostas[i] ?? ''} onChange={(v) => responder(i, v)} disabled={travado}
                                aria-label={`${campo.rotulo} — ${dimensao.rotulo}`}
                                options={[{ value: '', label: 'Selecione' }, ...dimensao.opcoes.map((o) => ({ value: o.valor, label: `${o.rotulo} (${o.letra})` }))]}
                            />
                        </label>
                    );
                })}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <Badge tone={sigla ? 'success' : 'neutral'}>
                    {sigla || 'Responda as quatro dimensões'}
                </Badge>
                <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{escala.ajuda}</span>
            </div>
        </div>
    );
}

/* ------------------------------------------------------------- tabela */

function TabelaCampo({ campo, valor, onResponder, desabilitado }: {
    campo: CampoDeFicha; valor: string[][]; onResponder: (v: RespostaDaFicha) => void; desabilitado: boolean;
}) {
    const colunas = (campo.colunas ?? []).filter((c) => c.trim());
    const linhas = (campo.linhas ?? []).filter((l) => l.trim());
    if (!colunas.length || !linhas.length) {
        return <SemConfiguracao texto="Dê nome a pelo menos uma linha e uma coluna." />;
    }

    const celula = (l: number, c: number) => valor[l]?.[c] ?? '';
    const mudar = (l: number, c: number, texto: string) => {
        const matriz = linhas.map((_, i) => colunas.map((__, k) => valor[i]?.[k] ?? ''));
        matriz[l]![c] = texto;
        onResponder(matriz);
    };

    return (
        <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <caption style={{ captionSide: 'top', textAlign: 'left', padding: '0 0 6px', fontSize: 11.5, color: 'var(--text-tertiary)' }}>
                    {campo.rotulo}
                </caption>
                <thead>
                    <tr>
                        <th style={{ width: 140, padding: '8px 12px', borderBottom: '1px solid var(--border)' }} />
                        {colunas.map((c) => (
                            <th key={c} style={{ textAlign: 'left', padding: '8px 12px', fontSize: 11.5, fontWeight: 600, color: 'var(--text-tertiary)', borderBottom: '1px solid var(--border)' }}>
                                {c}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {linhas.map((linha, l) => (
                        <tr key={linha}>
                            <th scope="row" style={{ textAlign: 'left', padding: '6px 12px', fontSize: 12.5, fontWeight: 600, borderTop: '1px solid var(--border-subtle)' }}>
                                {linha}
                            </th>
                            {colunas.map((coluna, c) => (
                                <td key={coluna} style={{ padding: 4, borderTop: '1px solid var(--border-subtle)' }}>
                                    <Input
                                        size="sm" value={celula(l, c)} disabled={desabilitado}
                                        onChange={(v) => mudar(l, c, v)}
                                        aria-label={`${linha} · ${coluna}`}
                                    />
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

/* ---------------------------------------------------------------- IMC */

function ImcCampo({ campo, valor, onResponder, desabilitado }: {
    campo: CampoDeFicha; valor: RespostaDeBloco; onResponder: (v: RespostaDaFicha) => void; desabilitado: boolean;
}) {
    const idPeso = useId();
    const idAltura = useId();

    const peso = valor.peso === undefined ? '' : String(valor.peso);
    const altura = valor.altura === undefined ? '' : String(valor.altura);
    const resultado = calcularImc(Number(peso) || undefined, Number(altura) || undefined);
    const sexo = campo.sexo ?? '';
    const silhueta = resultado ? silhuetaDoImc(resultado.classe, sexo) : '';

    const mudar = (chave: 'peso' | 'altura', bruto: string) => {
        const proximo: RespostaDeBloco = { ...valor, [chave]: bruto === '' ? undefined : Number(bruto) };
        const r = calcularImc(Number(proximo.peso) || undefined, Number(proximo.altura) || undefined);
        /* O resultado é gravado junto com as medidas: o prontuário precisa dizer
           o que foi lido no dia, e não recalcular anos depois com uma tabela que
           pode ter mudado. */
        onResponder(r ? { ...proximo, imc: r.valor, classe: r.classe, rotulo: r.rotulo } : proximo);
    };

    return (
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <label htmlFor={idPeso} style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12.5, fontWeight: 500, color: 'var(--text-secondary)' }}>
                Peso (kg)
                <Input id={idPeso} type="number" value={peso} onChange={(v) => mudar('peso', v)} disabled={desabilitado} style={{ width: 120 }} />
            </label>
            <label htmlFor={idAltura} style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12.5, fontWeight: 500, color: 'var(--text-secondary)' }}>
                Altura (m)
                <Input id={idAltura} type="number" step="0.01" value={altura} onChange={(v) => mudar('altura', v)} disabled={desabilitado} style={{ width: 120 }} />
            </label>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 44 }}>
                {silhueta && <img src={silhueta} alt="" style={{ height: 56 }} />}
                {resultado ? (
                    <span style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <strong style={{ fontSize: 18, fontWeight: 600, letterSpacing: '-0.01em' }}>
                            {resultado.valor.toFixed(1).replace('.', ',')}
                        </strong>
                        <Badge tone={resultado.classe === 'eutrofico' ? 'success' : 'warning'}>{resultado.rotulo}</Badge>
                    </span>
                ) : (
                    <span style={{ fontSize: 12.5, color: 'var(--text-tertiary)' }}>Informe peso e altura.</span>
                )}
            </div>
        </div>
    );
}

/* --------------------------------------------------------- perimetria */

function PerimetriaCampo({ campo, valor, onResponder, desabilitado }: {
    campo: CampoDeFicha; valor: RespostaDeBloco; onResponder: (v: RespostaDaFicha) => void; desabilitado: boolean;
}) {
    const sexo = campo.sexo ?? '';
    const resultado = calcularPerimetria(Number(valor.Cintura) || undefined, Number(valor.Quadril) || undefined, sexo);

    const mudar = (medida: string, bruto: string) => {
        const proximo: RespostaDeBloco = { ...valor, [medida]: bruto === '' ? undefined : Number(bruto) };
        const r = calcularPerimetria(Number(proximo.Cintura) || undefined, Number(proximo.Quadril) || undefined, sexo);
        onResponder(r ? { ...proximo, rcq: r.rcq, ...(r.risco ? { risco: r.risco } : {}) } : proximo);
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-secondary)' }}>
                Fita métrica inelástica, sem compressão do tecido. Medidas em centímetros.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 10 }}>
                {MEDIDAS_DA_PERIMETRIA.map((medida) => (
                    <label key={medida} style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12.5, fontWeight: 500, color: 'var(--text-secondary)' }}>
                        {medida}
                        <Input
                            type="number" size="sm" disabled={desabilitado}
                            value={valor[medida] === undefined ? '' : String(valor[medida])}
                            onChange={(v) => mudar(medida, v)}
                            aria-label={`${campo.rotulo} — ${medida}`}
                        />
                    </label>
                ))}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                {resultado ? (
                    <>
                        <Badge tone="info">RCQ {resultado.rcq.toFixed(2).replace('.', ',')}</Badge>
                        {resultado.risco
                            ? <Badge tone={resultado.risco === 'aumentado' ? 'warning' : 'success'}>
                                {resultado.risco === 'aumentado' ? 'Risco aumentado' : 'Dentro do esperado'}
                            </Badge>
                            : (
                                /* Os pontos de corte são diferentes por sexo. Sem ele, a
                                   razão sai e a classificação não — usar um corte para
                                   todo mundo classificaria metade das pessoas errado. */
                                <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>
                                    Informe o sexo na configuração do bloco para classificar o risco.
                                </span>
                            )}
                    </>
                ) : (
                    <span style={{ fontSize: 12.5, color: 'var(--text-tertiary)' }}>Cintura e quadril calculam a relação.</span>
                )}
            </div>
        </div>
    );
}

/* -------------------------------------------------------- adipometria */

/** A chave de uma leitura. Estável: prontuário antigo é lido por ela. */
function chaveDaLeitura(dobra: string, n: number): string {
    return `${dobra} ${String(n)}`;
}

/**
 * Adipometria: três leituras por dobra, e vale a mediana.
 *
 * A tela mostra as três porque **é assim que se mede** — uma leitura só não é
 * adipometria, é um número. E mostra a mediana ao lado, ao vivo, para o
 * profissional ver na hora quando as três discordam muito entre si: é o sinal
 * de pega errada, e ele só aparece se as três estiverem à vista.
 *
 * A equação e a fonte ficam escritas embaixo do resultado. Percentual de
 * gordura sem dizer por qual protocolo saiu é um número que ninguém consegue
 * conferir nem comparar com a medição do mês passado.
 */
function AdipometriaCampo({ campo, valor, onResponder, desabilitado }: {
    campo: CampoDeFicha; valor: RespostaDeBloco; onResponder: (v: RespostaDaFicha) => void; desabilitado: boolean;
}) {
    const idIdade = useId();
    const idPeso = useId();
    const protocolo = protocoloPorSlug(campo.protocolo) ?? PROTOCOLOS_DE_ADIPOMETRIA[0]!;
    const sexo = campo.sexo ?? '';
    const dobras = dobrasDoProtocolo(protocolo, sexo);

    const medianas = Object.fromEntries(dobras.map((d) => [
        d, medianaDaDobra([1, 2, 3].map((n) => {
            const bruto = valor[chaveDaLeitura(d, n)];
            return typeof bruto === 'number' ? bruto : undefined;
        })),
    ]));

    const idade = typeof valor['idade'] === 'number' ? valor['idade'] : undefined;
    const peso = typeof valor['peso'] === 'number' ? valor['peso'] : undefined;
    const resultado = calcularAdipometria(protocolo, medianas, sexo, idade, peso);
    const classificacao = classificarPercentual(resultado?.percentual, campo.faixas);

    const mudar = (chave: string, bruto: string) => {
        const proximo: RespostaDeBloco = { ...valor, [chave]: bruto === '' ? undefined : Number(bruto) };
        const novasMedianas = Object.fromEntries(dobras.map((d) => [
            d, medianaDaDobra([1, 2, 3].map((n) => {
                const v = proximo[chaveDaLeitura(d, n)];
                return typeof v === 'number' ? v : undefined;
            })),
        ]));
        const novaIdade = typeof proximo['idade'] === 'number' ? proximo['idade'] : undefined;
        const novoPeso = typeof proximo['peso'] === 'number' ? proximo['peso'] : undefined;
        const r = calcularAdipometria(protocolo, novasMedianas, sexo, novaIdade, novoPeso);

        /* O resultado é gravado junto com as leituras, com o protocolo que o
           produziu. O prontuário precisa dizer o que foi lido no dia — e por
           qual equação —, e não recalcular anos depois com outra. */
        const classe = classificarPercentual(r?.percentual, campo.faixas);
        onResponder(r
            ? {
                ...proximo, protocolo: protocolo.slug, soma: r.soma,
                ...(classe ? { classificacao: classe } : {}),
                ...(r.densidade === undefined ? {} : { densidade: r.densidade }),
                ...(r.percentual === undefined ? {} : { percentual: r.percentual }),
                ...(r.massaGorda === undefined ? {} : { massaGorda: r.massaGorda }),
                ...(r.massaMagra === undefined ? {} : { massaMagra: r.massaMagra }),
            }
            : { ...proximo, protocolo: protocolo.slug });
    };

    const faltaSexoOuIdade = protocolo.exigeSexoEIdade && (!sexo || !idade);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-secondary)' }}>
                {protocolo.descricao} Três leituras por dobra, no lado direito, com o adipômetro
                perpendicular à pele. Medidas em milímetros — vale a mediana das três.
            </p>

            {protocolo.exigeSexoEIdade && (
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    <label htmlFor={idIdade} style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12.5, fontWeight: 500, color: 'var(--text-secondary)' }}>
                        Idade (anos)
                        <Input
                            id={idIdade} type="number" size="sm" disabled={desabilitado} style={{ width: 110 }}
                            value={idade === undefined ? '' : String(idade)}
                            onChange={(v) => mudar('idade', v)}
                            aria-label={`${campo.rotulo} — idade`}
                        />
                    </label>
                    <label htmlFor={idPeso} style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12.5, fontWeight: 500, color: 'var(--text-secondary)' }}>
                        Peso (kg) — opcional
                        <Input
                            id={idPeso} type="number" size="sm" disabled={desabilitado} style={{ width: 130 }}
                            value={peso === undefined ? '' : String(peso)}
                            onChange={(v) => mudar('peso', v)}
                            aria-label={`${campo.rotulo} — peso`}
                        />
                    </label>
                </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(240px,100%),1fr))', gap: 10 }}>
                {dobras.map((dobra) => (
                    <div key={dobra} style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                        <span style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--text-secondary)' }}>{dobra}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            {[1, 2, 3].map((n) => (
                                <Input
                                    key={n} type="number" size="sm" disabled={desabilitado}
                                    value={valor[chaveDaLeitura(dobra, n)] === undefined ? '' : String(valor[chaveDaLeitura(dobra, n)])}
                                    onChange={(v) => mudar(chaveDaLeitura(dobra, n), v)}
                                    aria-label={`${campo.rotulo} — ${dobra}, leitura ${String(n)}`}
                                />
                            ))}
                            <span
                                style={{ minWidth: 52, fontSize: 12, color: medianas[dobra] === undefined ? 'var(--text-tertiary)' : 'var(--text-primary)' }}
                                aria-label={`${campo.rotulo} — ${dobra}, mediana`}
                            >
                                {medianas[dobra] === undefined ? '—' : `${String(medianas[dobra]).replace('.', ',')} mm`}
                            </span>
                        </div>
                    </div>
                ))}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                {resultado ? (
                    <>
                        <Badge tone="info">
                            Somatório {resultado.soma.toFixed(1).replace('.', ',')} mm
                            {resultado.medidas < resultado.exigidas && ` (${String(resultado.medidas)} de ${String(resultado.exigidas)})`}
                        </Badge>
                        {resultado.percentual !== undefined && (
                            <Badge tone="brand">{resultado.percentual.toFixed(1).replace('.', ',')}% de gordura</Badge>
                        )}
                        {classificacao && <Badge tone="neutral">{classificacao}</Badge>}
                        {resultado.massaGorda !== undefined && resultado.massaMagra !== undefined && (
                            <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
                                {resultado.massaGorda.toFixed(1).replace('.', ',')} kg de gordura ·{' '}
                                {resultado.massaMagra.toFixed(1).replace('.', ',')} kg de massa magra
                            </span>
                        )}
                    </>
                ) : (
                    <span style={{ fontSize: 12.5, color: 'var(--text-tertiary)' }}>Informe as dobras para somar.</span>
                )}
            </div>

            {faltaSexoOuIdade && (
                /* A equação de Pollock foi derivada por sexo e leva a idade dentro
                   dela. Sem os dois, o somatório continua saindo — o percentual,
                   não. Usar uma equação de homem numa mulher erra por vários
                   pontos, e erra em silêncio. */
                <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>
                    {sexo
                        ? 'Informe a idade para o percentual de gordura — a equação a leva dentro dela.'
                        : 'Informe o sexo na configuração do bloco e a idade aqui: a equação é derivada por sexo.'}
                </span>
            )}

            <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>
                {protocolo.fonte}
                {resultado?.percentual !== undefined && !classificacao
                    && ' Sem faixa de classificação: a clínica não configurou uma. Não há tabela oficial de'
                    + ' percentual de gordura como a da OMS para o IMC — quem adota uma é a clínica.'}
            </span>
        </div>
    );
}

/* ----------------------------------------------------------- arquivos */

function ArquivoCampo({ rotulo, valor, onResponder, desabilitado, onEnviarArquivo, onAbrirArquivo }: {
    rotulo: string;
    valor: ArquivoDaFicha[];
    onResponder: (v: RespostaDaFicha) => void;
    desabilitado: boolean;
    onEnviarArquivo?: (arquivo: File) => Promise<ArquivoDaFicha>;
    onAbrirArquivo?: (caminho: string) => Promise<string>;
}) {
    const idEntrada = useId();
    const [enviando, setEnviando] = useState(false);
    const [erro, setErro] = useState<string | null>(null);

    const escolher = async (lista: FileList | null) => {
        if (!lista?.length || !onEnviarArquivo) return;
        setEnviando(true);
        setErro(null);
        try {
            const enviados: ArquivoDaFicha[] = [];
            for (const arquivo of Array.from(lista)) {
                enviados.push(await onEnviarArquivo(arquivo));
            }
            onResponder([...valor, ...enviados]);
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível enviar.');
        } finally {
            setEnviando(false);
        }
    };

    /* Anexar sem poder conferir é anexar às cegas: a foto errada só apareceria
       no prontuário, depois de a ficha estar gravada. */
    const abrir = async (caminho: string) => {
        if (!onAbrirArquivo) return;
        setErro(null);
        try {
            const aberto = await abrirAnexo(() => onAbrirArquivo(caminho));
            if (!aberto) setErro('Permita pop-ups para abrir o anexo.');
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível abrir o anexo.');
        }
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}

            {valor.map((arquivo) => (
                <div key={arquivo.caminho} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)' }}>
                    <Icon name={arquivo.tipo.startsWith('image/') ? 'image' : 'file-text'} size={15} />
                    <span style={{ flex: 1, minWidth: 0, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {arquivo.nome}
                    </span>
                    <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{Math.max(1, Math.round(arquivo.bytes / 1024))} KB</span>
                    {onAbrirArquivo && (
                        <IconButton
                            size="sm" icon={<Icon name="eye" size={14} />} label={`Abrir ${arquivo.nome}`}
                            onClick={() => void abrir(arquivo.caminho)}
                        />
                    )}
                    {!desabilitado && (
                        <IconButton
                            size="sm" icon={<Icon name="x" size={14} />} label={`Remover ${arquivo.nome}`}
                            onClick={() => onResponder(valor.filter((a) => a.caminho !== arquivo.caminho))}
                        />
                    )}
                </div>
            ))}

            <div>
                <input
                    id={idEntrada} type="file" multiple disabled={desabilitado || enviando}
                    accept="image/jpeg,image/png,image/webp,image/heic,application/pdf"
                    onChange={(e) => void escolher(e.target.files)}
                    style={{ display: 'none' }}
                />
                <Button
                    size="sm" variant="secondary" disabled={desabilitado || enviando}
                    iconLeft={<Icon name="paperclip" size={14} />}
                    onClick={() => document.getElementById(idEntrada)?.click()}
                >
                    {enviando ? 'Enviando…' : desabilitado ? 'Anexar arquivo' : `Anexar em ${rotulo}`}
                </Button>
            </div>

            <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>
                Foto, exame ou PDF, até 20 MB. Fica no cofre da clínica — o endereço é assinado e vence.
            </span>
        </div>
    );
}
